import { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  ShoppingCart as CartIcon,
  Receipt as ReceiptIcon,
  School as SchoolIcon,
  Category as CategoryIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Event as EventIcon,
  LocalLibrary as LibraryIcon,
} from '@mui/icons-material';
import { useData } from '../../contexts/DataContext';

interface DonutChartProps {
  data: Array<{ label: string; value: number; color: string }>;
  size?: number;
  thickness?: number;
}

function DonutChart({ data, size = 200, thickness = 40 }: DonutChartProps) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  let currentAngle = -90;

  return (
    <Box sx={{ position: 'relative', width: size, height: size, margin: '0 auto' }}>
      <svg width={size} height={size}>
        {data.map((item, index) => {
          const percentage = (item.value / total) * 100;
          const angle = (percentage / 100) * 360;
          const startAngle = currentAngle;
          const endAngle = startAngle + angle;

          const startRad = (startAngle * Math.PI) / 180;
          const endRad = (endAngle * Math.PI) / 180;

          const x1 = center + radius * Math.cos(startRad);
          const y1 = center + radius * Math.sin(startRad);
          const x2 = center + radius * Math.cos(endRad);
          const y2 = center + radius * Math.sin(endRad);

          const largeArc = angle > 180 ? 1 : 0;

          const pathData = [
            `M ${center + (radius - thickness / 2) * Math.cos(startRad)} ${center + (radius - thickness / 2) * Math.sin(startRad)}`,
            `A ${radius - thickness / 2} ${radius - thickness / 2} 0 ${largeArc} 1 ${center + (radius - thickness / 2) * Math.cos(endRad)} ${center + (radius - thickness / 2) * Math.sin(endRad)}`,
          ].join(' ');

          currentAngle = endAngle;

          return (
            <path
              key={index}
              d={pathData}
              fill="none"
              stroke={item.color}
              strokeWidth={thickness}
              strokeLinecap="round"
            />
          );
        })}
      </svg>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          {total}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Total
        </Typography>
      </Box>
    </Box>
  );
}

interface BarChartProps {
  data: Array<{ label: string; value: number; color?: string }>;
  height?: number;
  showValues?: boolean;
}

function BarChart({ data, height = 300, showValues = true }: BarChartProps) {
  const maxValue = Math.max(...data.map(d => d.value));
  const barHeight = 40;
  const spacing = 16;
  const chartHeight = data.length * (barHeight + spacing);

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      {data.map((item, index) => {
        const percentage = (item.value / maxValue) * 100;
        return (
          <Box key={index} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" fontWeight={500}>
                {item.label}
              </Typography>
              {showValues && (
                <Typography variant="body2" fontWeight="bold" color="primary">
                  ${item.value.toFixed(2)}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                width: '100%',
                height: 32,
                bgcolor: 'grey.100',
                borderRadius: 2,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${percentage}%`,
                  height: '100%',
                  background: item.color || 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
                  borderRadius: 2,
                  transition: 'width 0.6s ease-in-out',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  px: 1,
                }}
              >
                <Typography variant="caption" fontWeight="bold" sx={{ color: 'white' }}>
                  {percentage.toFixed(1)}%
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  subtitle?: string;
}

function MetricCard({ title, value, icon, color, trend, subtitle }: MetricCardProps) {
  return (
    <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                {trend.isPositive ? (
                  <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
                ) : (
                  <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
                )}
                <Typography
                  variant="caption"
                  sx={{ color: trend.isPositive ? 'success.main' : 'error.main', fontWeight: 600 }}
                >
                  {trend.value}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  vs last month
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: color,
              width: 56,
              height: 56,
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
}

export function MetricsDashboard() {
  const { orders: mockOrders, orderItems: mockOrderItems, students: mockStudents, parents: mockParents, communityMembers: mockCommunityMembers, fees: mockFees, feeCategories: mockFeeCategories } = useData();
  const statistics = useMemo(() => {
    const completedOrders = mockOrders.filter(o => o.paymentStatus === 'completed');
    const totalRevenue = completedOrders.reduce((sum, o) => sum + o.total, 0);

    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const thisYear = new Date(now.getFullYear(), 0, 1);

    const thisMonthOrders = completedOrders.filter(o => new Date(o.createdAt) >= thisMonth);
    const lastMonthOrders = completedOrders.filter(
      o => new Date(o.createdAt) >= lastMonth && new Date(o.createdAt) < thisMonth
    );
    const thisYearOrders = completedOrders.filter(o => new Date(o.createdAt) >= thisYear);

    const monthRevenue = thisMonthOrders.reduce((sum, o) => sum + o.total, 0);
    const lastMonthRevenue = lastMonthOrders.reduce((sum, o) => sum + o.total, 0);
    const yearRevenue = thisYearOrders.reduce((sum, o) => sum + o.total, 0);

    const revenueTrend = lastMonthRevenue > 0
      ? ((monthRevenue - lastMonthRevenue) / lastMonthRevenue * 100)
      : 100;

    const categoryRevenue = new Map<string, number>();
    mockOrderItems.forEach(item => {
      const order = mockOrders.find(o => o.id === item.orderId);
      if (order?.paymentStatus === 'completed') {
        const current = categoryRevenue.get(item.feeCategory) || 0;
        categoryRevenue.set(item.feeCategory, current + item.totalPrice);
      }
    });

    const paymentMethods = new Map<string, number>();
    completedOrders.forEach(order => {
      const method = order.paymentMethod || 'N/A';
      paymentMethods.set(method, (paymentMethods.get(method) || 0) + 1);
    });

    const userTypeDistribution = new Map<string, number>();
    completedOrders.forEach(order => {
      const type = order.userType;
      userTypeDistribution.set(type, (userTypeDistribution.get(type) || 0) + 1);
    });

    const recentOrders = [...mockOrders]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5);

    const feeItemCounts = new Map<string, { count: number; revenue: number }>();
    mockOrderItems.forEach(item => {
      const order = mockOrders.find(o => o.id === item.orderId);
      if (order?.paymentStatus === 'completed') {
        const current = feeItemCounts.get(item.feeName) || { count: 0, revenue: 0 };
        feeItemCounts.set(item.feeName, {
          count: current.count + item.quantity,
          revenue: current.revenue + item.totalPrice,
        });
      }
    });

    const topItems = Array.from(feeItemCounts.entries())
      .sort((a, b) => b[1].revenue - a[1].revenue)
      .slice(0, 5);

    return {
      totalRevenue,
      monthRevenue,
      yearRevenue,
      revenueTrend,
      totalOrders: completedOrders.length,
      monthOrders: thisMonthOrders.length,
      totalStudents: mockStudents.length,
      totalParents: mockParents.length,
      totalCommunity: mockCommunityMembers.length,
      totalUsers: mockStudents.length + mockParents.length + mockCommunityMembers.length,
      totalFees: mockFees.length,
      categoryRevenue: Array.from(categoryRevenue.entries()).sort((a, b) => b[1] - a[1]),
      paymentMethods: Array.from(paymentMethods.entries()).sort((a, b) => b[1] - a[1]),
      userTypeDistribution: Array.from(userTypeDistribution.entries()).sort((a, b) => b[1] - a[1]),
      recentOrders,
      topItems,
      averageOrderValue: completedOrders.length > 0 ? totalRevenue / completedOrders.length : 0,
    };
  }, [mockOrders, mockOrderItems, mockStudents, mockParents, mockCommunityMembers, mockFees, mockFeeCategories]);

  const getCategoryIcon = (category: string) => {
    if (category.includes('Event')) return <EventIcon />;
    if (category.includes('Transportation')) return <CartIcon />;
    if (category.includes('Materials') || category.includes('Supplies')) return <LibraryIcon />;
    return <CategoryIcon />;
  };

  const chartColors = {
    category1: '#0f766e',
    category2: '#14b8a6',
    category3: '#5eead4',
    category4: '#2dd4bf',
    category5: '#0d9488',
    category6: '#99f6e4',
    category7: '#134e4a',
    category8: '#06b6d4',
  };

  const userDistributionData = [
    { label: 'Students', value: statistics.totalStudents, color: '#0f766e' },
    { label: 'Parents', value: statistics.totalParents, color: '#14b8a6' },
    { label: 'Community', value: statistics.totalCommunity, color: '#5eead4' },
  ];

  const paymentMethodChartData = statistics.paymentMethods.map(([method, count], index) => {
    const colors = ['#0f766e', '#14b8a6', '#5eead4', '#2dd4bf', '#0d9488'];
    return { label: method, value: count, color: colors[index % colors.length] };
  });

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      <Box sx={{ mb: { xs: 2, sm: 4 } }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Metrics Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          Overview of key performance indicators and system metrics
        </Typography>
      </Box>

      <Box data-tour="admin-metrics" sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mb: 3, '@media (min-width: 600px)': { gridTemplateColumns: 'repeat(2, 1fr)', gap: 3 }, '@media (min-width: 900px)': { gridTemplateColumns: 'repeat(4, 1fr)' } }}>
        <Card sx={{ minWidth: 0 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <MoneyIcon sx={{ color: '#0f766e', mr: 1, fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                Total Revenue
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ mb: 0.5, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              ${statistics.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All time
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 0 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <ReceiptIcon sx={{ color: '#14b8a6', mr: 1, fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                This Month
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ mb: 0.5, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              ${statistics.monthRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {statistics.revenueTrend >= 0 ? (
                <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
              ) : (
                <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
              )}
              <Typography
                variant="body2"
                sx={{ color: statistics.revenueTrend >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}
              >
                {Math.abs(Math.round(statistics.revenueTrend))}%
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 0 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CartIcon sx={{ color: '#5eead4', mr: 1, fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                Total Orders
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ mb: 0.5, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              {statistics.totalOrders.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {statistics.monthOrders} this month
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 0 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PeopleIcon sx={{ color: '#2dd4bf', mr: 1, fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                Registered Users
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ mb: 0.5, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              {statistics.totalUsers.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {statistics.totalStudents} students
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mb: 3, '@media (min-width: 600px)': { gridTemplateColumns: 'repeat(2, 1fr)', gap: 3 }, '@media (min-width: 900px)': { gridTemplateColumns: 'repeat(4, 1fr)' } }}>
        <Card sx={{ minWidth: 0 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                <SchoolIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {statistics.totalStudents}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Students
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 0 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'secondary.main', width: 36, height: 36 }}>
                <PeopleIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {statistics.totalParents}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Parents
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 0 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'info.main', width: 36, height: 36 }}>
                <PeopleIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {statistics.totalCommunity}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Community
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 0 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'success.main', width: 36, height: 36 }}>
                <MoneyIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  ${statistics.averageOrderValue.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg Order Value
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                User Distribution
              </Typography>
              <Box sx={{ mt: 3, mb: 3 }}>
                <DonutChart data={userDistributionData} size={240} thickness={50} />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                {userDistributionData.map((item) => (
                  <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        bgcolor: item.color,
                        borderRadius: '50%',
                      }}
                    />
                    <Typography variant="body2">
                      {item.label}: <strong>{item.value}</strong>
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Revenue by Category
              </Typography>
              <Box sx={{ mt: 3 }}>
                <BarChart
                  data={statistics.categoryRevenue.slice(0, 5).map(([category, revenue], index) => {
                    const colors = ['#0f766e', '#14b8a6', '#5eead4', '#2dd4bf', '#0d9488'];
                    return {
                      label: category,
                      value: revenue,
                      color: colors[index % colors.length],
                    };
                  })}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Payment Methods
              </Typography>
              <Box sx={{ mt: 3, mb: 3 }}>
                <DonutChart data={paymentMethodChartData} size={240} thickness={50} />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                {paymentMethodChartData.map((item) => (
                  <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        bgcolor: item.color,
                        borderRadius: '50%',
                      }}
                    />
                    <Typography variant="body2">
                      {item.label}: <strong>{item.value}</strong>
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Top Selling Items
              </Typography>
              <Box sx={{ mt: 3 }}>
                <BarChart
                  data={statistics.topItems.map(([name, data], index) => {
                    const colors = ['#0f766e', '#14b8a6', '#5eead4', '#2dd4bf', '#0d9488'];
                    return {
                      label: name,
                      value: data.revenue,
                      color: colors[index % colors.length],
                    };
                  })}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
            Orders by User Type
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3 }}>
            {statistics.userTypeDistribution.map(([type, count]) => {
              const percentage = (count / statistics.totalOrders) * 100;
              const colors = {
                student: '#0f766e',
                parent: '#14b8a6',
                community: '#5eead4',
                guest: '#2dd4bf',
              };
              return (
                <Card key={type} sx={{ border: 1, borderColor: 'divider' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body1" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                        {type}
                      </Typography>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: colors[type as keyof typeof colors],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <PeopleIcon sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                    </Box>
                    <Typography variant="h4" fontWeight="bold" color={colors[type as keyof typeof colors]}>
                      {count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {percentage.toFixed(1)}% of orders
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Recent Orders
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order #</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statistics.recentOrders.map((order) => (
                  <TableRow key={order.id} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {order.orderNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{order.userName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {order.userEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.userType}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        ${order.total.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.paymentStatus}
                        size="small"
                        color={order.paymentStatus === 'completed' ? 'success' : 'default'}
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
